import { Trans, useTranslation } from 'react-i18next';

import {
    Anchor,
    Body1,
    FilledCard,
    Heading2,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import { PricingInfo } from '@phx/myphx-lib';

import { getFragment } from '../../../graphql';
import { useFlags } from '../../../hooks/use-flags';
import { isPriceableOffer } from '../../../util/offers/is-priceable-offer';

import { GenericSwapPricingDesc } from './GenericSwapPricingDesc';
import { NoPricing } from './NoPricing';
import { PricingDisclosure } from './PricingDisclosure';
import type { PricingProps } from './shared';

export const MailOrderPricing = ({ offer, genericSwap }: PricingProps) => {
    const { t } = useTranslation();
    const [opened, { toggle }] = useDisclosure();
    const { costPlusDrugsEnabled } = useFlags();

    const hasOffer = isPriceableOffer(offer);

    const priceSection = hasOffer ? (
        <PricingInfo
            option={offer.pricingDetail.option}
            value={offer.pricingDetail.value}
            details={offer.pricingDetail.details ?? ''}
        />
    ) : (
        <NoPricing />
    );

    const shippingPrice = hasOffer ? offer.shippingPrices[0] : undefined;

    const shippingMethod =
        getFragment(shippingPrice)?.shippingMethod.toLowerCase();

    return (
        <Stack gap="xxs" data-testid="price-quote-card">
            <Stack gap="sm">
                <Heading2>{t('pricing.subHeader')}</Heading2>
                {hasOffer &&
                    costPlusDrugsEnabled &&
                    (shippingMethod === 'cold chain' ? (
                        <Body1>
                            <Trans
                                i18nKey="pricing.mailOrder.shippingFee.coldChain"
                                values={{ shippingMethod }}
                                components={{
                                    1: <Anchor onClick={toggle} />,
                                }}
                            />
                        </Body1>
                    ) : (
                        <Body1>
                            {t('pricing.mailOrder.shippingFee.standard', {
                                shippingMethod: shippingMethod ?? 'standard',
                            })}
                        </Body1>
                    ))}
                {genericSwap && (
                    <GenericSwapPricingDesc
                        brandName={genericSwap.brandName}
                        genericName={genericSwap.genericName}
                    />
                )}
                <FilledCard color="gray">{priceSection}</FilledCard>
                <PricingDisclosure />
            </Stack>
            <Modal
                title={t('pricing.coldChain.title')}
                opened={opened}
                onClose={toggle}
            >
                <Stack gap="sm">
                    <Body1>{t('pricing.coldChain.description.partOne')}</Body1>
                    <Body1>{t('pricing.coldChain.description.partTwo')}</Body1>
                </Stack>
            </Modal>
            {/* Remove breakdown until actual data can be mapped
            <PricingInfo
                option={t('pricing.pricingInfo.deliveryFee')}
                value={`${formatToLocalizedCurrency(0)}`}
                details={t('pricing.pricingInfo.details')}
            /> */}
            {/* <FilledCard color="gray">
                <PricingInfo
                    option={t('pricing.pricingInfo.youPay')}
                    value={offer.pricingDetail.value}
                />
            </FilledCard> */}
        </Stack>
    );
};
